import { API_BASE_URL } from '../lib/constants';
import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AuthContext } from '../contexts/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useNotification } from '../contexts/NotificationContext';
import { Edit, Trash2, Plus, Search, ChevronLeft, ChevronRight, Download, FileText, Phone, Mail, User, Stethoscope, Activity } from 'lucide-react';

const UserList = () => {
  const { userRole, permissions } = useContext(AuthContext);
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();
  const currentPage = parseInt(searchParams.get('page') || '0', 10);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);

  const hasPermission = (perm) => {
    if ((userRole || '').toLowerCase() === 'admin') return true;
    return (permissions || []).includes(perm);
  };
  const rowsPerPage = 5;

  // Simulate API call
  useEffect(() => {
    axios.get(`${API_BASE_URL}/get_users`)  // Replace with your actual API endpoint
      .then((response) => {
        if (Array.isArray(response.data.data)) {
          setData(response.data.data);
        } else {
          showNotification('error', 'API response data is not in the expected format');
        }
      })
      .catch((error) => {
        console.error('Error fetching user data:', error);
        showNotification('error', 'Failed to fetch user data');
      });
  }, [showNotification]);

  const filteredUsers = data.filter(user =>
    (user.fullname || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
    (user.email || "").toLowerCase().includes(searchTerm.toLowerCase())
  );


  // Paginated users
  const totalPages = Math.ceil(filteredUsers.length / rowsPerPage);
  const paginatedUsers = filteredUsers.slice(
    currentPage * rowsPerPage,
    currentPage * rowsPerPage + rowsPerPage
  );
  const handleEdit = (user_id) => {
    navigate(`/editUser/${user_id}`);
  };

  const handleDelete = (user) => {
    showNotification('confirm', `Are you sure you want to delete user ${user.fullname}?`, async () => {
      try {
        const response = await axios.delete(`${API_BASE_URL}/delete_user/${user.user_id}`);
        if (response.status === 200) {
          setData(prev => prev.filter(u => u.user_id !== user.user_id));
          showNotification('success', 'User deleted successfully');
        } else {
          showNotification('error', 'Failed to delete user');
        }
      } catch (error) {
        console.error("Error deleting user:", error);
        showNotification('error', error.response?.data?.message || 'Error occurred while deleting user');
      }
    });
  };





  const resetSearch = () => {
    setSearchTerm('');
    setSearchParams({ page: '0' });
  };

  if (loading) {
    return (
      <div className="w-full max-w-6xl mx-auto p-4">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded mb-4"></div>
          <div className="h-12 bg-gray-200 rounded mb-6"></div>
          <div className="space-y-3">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6 space-y-6">


      {/* Action bar */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
          <div className="relative w-full lg:w-2/3">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <input
              type="text"
              placeholder="Search users by name or email..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setSearchParams({ page: '0' });
              }}
              className="w-full pl-11 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 outline-none transition-all"
            />
            {searchTerm && (
              <button
                onClick={resetSearch}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            )}
          </div>

          {hasPermission('User Add') && (
            <div className="flex gap-3 w-full lg:w-auto">
              <button
                onClick={() => navigate('/addUser')}
                className="flex items-center gap-2 bg-teal-600 text-white px-4 py-3 rounded-lg hover:bg-teal-700 transition-colors font-medium flex-1 lg:flex-none justify-center"
              >
                <Plus className="h-4 w-4" />
                Add User
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Results summary */}
      {searchTerm && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-blue-800">
            Found {filteredUsers.length} user{filteredUsers.length !== 1 ? 's' : ''} matching "{searchTerm}"
          </p>
        </div>
      )}

      {/* Desktop Table */}
      <div className="hidden lg:block bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-teal-50 border-b border-teal-100">
                <th className="text-left p-4 font-semibold text-teal-900">S/No</th>
                <th className="text-left p-4 font-semibold text-teal-900">User Name</th>
                <th className="text-left p-4 font-semibold text-teal-900">Email</th>
                <th className="text-left p-4 font-semibold text-teal-900">Last Login</th>
                <th className="text-center p-4 font-semibold text-teal-900">Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedUsers.length === 0 ? (
                <tr>
                  <td colSpan={4} className="p-12 text-center">
                    <div className="flex flex-col items-center">
                      <Search className="h-16 w-16 text-gray-300 mb-4" />
                      <h3 className="text-lg font-medium text-gray-500 mb-2">No users found</h3>
                      <p className="text-gray-400 mb-4">Try adjusting your search criteria</p>
                      {searchTerm && (
                        <button
                          onClick={resetSearch}
                          className="text-teal-600 hover:text-teal-700 font-medium"
                        >
                          Clear search
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                paginatedUsers.map((user, index) => (
                  <tr
                    key={user.uhid_no}
                    className={`border-b border-gray-100 hover:bg-teal-50/50 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'
                      }`}
                  >
                    <td className="p-4">
                      <div className="font-medium text-gray-900">
                        {currentPage * rowsPerPage + index + 1}
                      </div>
                    </td>
                    <td className="p-4">
                      <div className="font-medium text-gray-900">{user.fullname}</div>
                    </td>
                    <td className="p-4">
                      <div className="flex items-center gap-2 text-gray-600">
                        <Mail className="h-4 w-4" />
                        <span className="break-words max-w-md">{user.email}</span>
                      </div>
                    </td>
                    <td className="p-4 text-xs text-gray-500">
                      {user.lastlogin || 'Never'}
                    </td>
                    <td className="p-4">
                      <div className="flex justify-center gap-1">
                        {hasPermission('User Edit') && (
                          <button
                            onClick={() => handleEdit(user.user_id)}
                            className="flex items-center gap-1 px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 border border-blue-200 rounded-lg transition-colors"
                            title="Edit User Information"
                          >
                            <Edit className="h-4 w-4" />
                            <span className="hidden sm:inline">Edit</span>
                          </button>
                        )}
                        {hasPermission('User Delete') && (
                          <button
                            onClick={() => handleDelete(user)}
                            className="flex items-center gap-1 px-3 py-2 text-sm text-red-600 hover:bg-red-50 border border-red-200 rounded-lg transition-colors"
                            title="Delete User"
                          >
                            <Trash2 className="h-4 w-4" />
                            <span className="hidden sm:inline">Delete</span>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Tablet Table */}
      <div className="lg:hidden space-y-4">
        {paginatedUsers.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
            <Search className="h-16 w-16 text-gray-300 mb-4 mx-auto" />
            <h3 className="text-lg font-medium text-gray-500 mb-2">No users found</h3>
            <p className="text-gray-400 mb-4">Try adjusting your search criteria</p>
            {searchTerm && (
              <button
                onClick={resetSearch}
                className="text-teal-600 hover:text-teal-700 font-medium"
              >
                Clear search
              </button>
            )}
          </div>
        ) : (
          paginatedUsers.map((user, index) => (
            <div key={user.uhid_no} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="bg-teal-100 text-teal-800 px-2 py-1 rounded text-sm font-medium">
                      #{currentPage * rowsPerPage + index + 1}
                    </span>
                  </div>
                  <h3 className="font-semibold text-gray-900 text-lg mb-1">{user.fullname}</h3>
                </div>
              </div>

              <div className="space-y-3 mb-4">
                <div className="flex items-center gap-3">
                  <Mail className="h-4 w-4 text-gray-400" />
                  <span className="text-gray-600 break-words">{user.email}</span>
                </div>
              </div>

              {/* Action buttons for mobile */}
              <div className="flex gap-2 pt-3 border-t border-gray-100">
                {hasPermission('User Edit') && (
                  <button
                    onClick={() => handleEdit(user.user_id)}

                    className="flex-1 flex items-center justify-center gap-2 py-2 px-3 text-blue-600 hover:bg-blue-50 border border-blue-200 rounded-lg transition-colors text-sm font-medium"
                    title="Edit User Information"
                  >
                    <Edit className="h-4 w-4" />
                    Edit
                  </button>
                )}
                {hasPermission('User Delete') && (
                  <button
                    onClick={() => handleDelete(user)}
                    className="flex-1 flex items-center justify-center gap-2 py-2 px-3 text-red-600 hover:bg-red-50 border border-red-200 rounded-lg transition-colors text-sm font-medium"
                    title="Delete User"
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {filteredUsers.length > 0 && totalPages > 1 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-sm text-gray-600">
              Showing {currentPage * rowsPerPage + 1} to {Math.min((currentPage + 1) * rowsPerPage, filteredUsers.length)} of {filteredUsers.length} users
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setSearchParams({ page: '0' })}
                disabled={currentPage === 0}
                className="px-3 py-2 text-sm rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                First
              </button>
              <button
                onClick={() => setSearchParams({ page: (currentPage - 1).toString() })}
                disabled={currentPage === 0}
                className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <span className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-50 rounded-lg">
                {currentPage + 1} of {totalPages}
              </span>
              <button
                onClick={() => setSearchParams({ page: (currentPage + 1).toString() })}
                disabled={currentPage >= totalPages - 1}
                className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
              <button
                onClick={() => setSearchParams({ page: (totalPages - 1).toString() })}
                disabled={currentPage >= totalPages - 1}
                className="px-3 py-2 text-sm rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                Last
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserList;
